import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  HStack,
  VStack,
  IconButton,
  useToast,
  Text,
  Box,
  Flex,
  Badge,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { useState } from 'react';

export function BlogCategoryModal({ categories, setCategories }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newName, setNewName] = useState('');
  const [newOrder, setNewOrder] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', order: '' });
  const toast = useToast();

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast({
        title: '카테고리명을 입력해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    try {
      const res = await axios.post('/admin/blog-categories', {
        name: newName,
        order: newOrder === '' ? categories.length : Number(newOrder),
      });
      setCategories(
        [...categories, res.data].sort((a, b) => a.order - b.order),
      );
      setNewName('');
      setNewOrder('');
      toast({ title: '추가되었습니다.', status: 'success', isClosable: true });
    } catch (error) {
      toast({
        title: error.response?.data?.message || '오류가 발생했습니다.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, order: String(cat.order) });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', order: '' });
  };

  const handleUpdate = async (id) => {
    if (!editForm.name.trim()) {
      toast({
        title: '카테고리명을 입력해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    try {
      const res = await axios.patch(`/admin/blog-categories/${id}`, {
        name: editForm.name,
        order: editForm.order === '' ? 0 : Number(editForm.order),
      });
      setCategories(
        categories
          .map((c) => (c.id === id ? res.data : c))
          .sort((a, b) => a.order - b.order),
      );
      setEditingId(null);
      toast({ title: '수정되었습니다.', status: 'success', isClosable: true });
    } catch (error) {
      toast({
        title: error.response?.data?.message || '오류가 발생했습니다.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/admin/blog-categories/${id}`);
      setCategories(categories.filter((c) => c.id !== id));
      toast({ title: '삭제되었습니다.', status: 'success', isClosable: true });
    } catch (error) {
      toast({
        title: error.response?.data?.message || '삭제 실패',
        status: 'error',
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button onClick={onOpen} colorScheme="blue">
        카테고리 관리
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>카테고리 관리</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {/* 추가 폼 */}
            <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
              <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
                새 카테고리 추가
              </Text>
              <Flex gap={2}>
                <Input
                  flex={1}
                  placeholder="카테고리명"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  bg="white"
                />
                <Input
                  w="70px"
                  placeholder="순서"
                  type="number"
                  value={newOrder}
                  onChange={(e) => setNewOrder(e.target.value)}
                  bg="white"
                />
                <Button colorScheme="green" onClick={handleAdd} px={6}>
                  추가
                </Button>
              </Flex>
            </Box>

            {/* 카테고리 목록 */}
            <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
              카테고리 목록 ({categories.length}개)
            </Text>
            {categories.length === 0 ? (
              <Box textAlign="center" py={8} color="gray.400">
                등록된 카테고리가 없습니다.
              </Box>
            ) : (
              <VStack align="stretch" spacing={2}>
                {categories.map((c) => (
                  <Box
                    key={c.id}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg={editingId === c.id ? 'blue.50' : 'white'}
                  >
                    {editingId === c.id ? (
                      <Flex gap={2} align="center">
                        <Input
                          flex={1}
                          size="sm"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                        />
                        <Input
                          w="60px"
                          size="sm"
                          type="number"
                          value={editForm.order}
                          onChange={(e) =>
                            setEditForm({ ...editForm, order: e.target.value })
                          }
                        />
                        <IconButton
                          size="sm"
                          colorScheme="green"
                          icon={<CheckIcon />}
                          onClick={() => handleUpdate(c.id)}
                          aria-label="저장"
                        />
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={<CloseIcon />}
                          onClick={handleCancelEdit}
                          aria-label="취소"
                        />
                      </Flex>
                    ) : (
                      <Flex justify="space-between" align="center">
                        <HStack>
                          <Text fontWeight="medium">{c.name}</Text>
                          <Badge colorScheme="gray" fontSize="xs">
                            순서: {c.order}
                          </Badge>
                        </HStack>
                        <HStack spacing={1}>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            icon={<EditIcon />}
                            onClick={() => handleEdit(c)}
                            aria-label="수정"
                          />
                          <IconButton
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            icon={<DeleteIcon />}
                            onClick={() => handleDelete(c.id)}
                            aria-label="삭제"
                          />
                        </HStack>
                      </Flex>
                    )}
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
